import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, CheckCheck, RefreshCw } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import RequireAdmin from "@/components/admin/RequireAdmin";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { useAdminI18n } from "@/lib/adminI18n";

function AdminNotificationsPageInner() {
  const { apiFetch } = useAdminAuth();
  const { t } = useAdminI18n();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/admin/notifications${unreadOnly ? "?unread_only=true" : ""}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || t("notifications.loadFailed", "Could not load notifications"));
      setItems(Array.isArray(data.items) ? data.items : []);
      setUnread(Number(data.unread) || 0);
    } catch (err) {
      toast.error(err?.message || t("notifications.loadFailed", "Could not load notifications"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [unreadOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const markRead = async (id) => {
    try {
      const res = await apiFetch(`/admin/notifications/${encodeURIComponent(id)}/read`, { method: "PATCH" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || t("notifications.markFailed", "Could not mark as read"));
      setItems((rows) => rows.map((row) => (row.id === id ? data.item : row)));
      setUnread((count) => Math.max(0, count - 1));
    } catch (err) {
      toast.error(err?.message || t("notifications.markFailed", "Could not mark as read"));
    }
  };

  const markAllRead = async () => {
    try {
      const res = await apiFetch("/admin/notifications/read-all", { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || t("notifications.markAllFailed", "Could not mark all as read"));
      toast.success(t("notifications.markedRead", "Notifications marked read"));
      load();
    } catch (err) {
      toast.error(err?.message || t("notifications.markAllFailed", "Could not mark all as read"));
    }
  };

  return (
    <AdminShell
      title={t("notifications.title", "Notifications")}
      description={t("notifications.description", "Security, approval, content, and system notices generated by website admin activity.")}
      actions={(
        <>
          <button type="button" onClick={() => setUnreadOnly((v) => !v)} className="admin-shell-action">
            <Bell className="h-4 w-4" />
            <span>{unreadOnly ? t("notifications.all", "All") : t("notifications.unreadCount", "Unread ({count})").replace("{count}", unread)}</span>
          </button>
          <button type="button" onClick={markAllRead} className="admin-shell-action">
            <CheckCheck className="h-4 w-4" />
            <span>{t("notifications.readAll", "Read all")}</span>
          </button>
          <button type="button" onClick={load} className="admin-shell-action">
            <RefreshCw className="h-4 w-4" />
            <span>{t("dashboard.refresh", "Refresh")}</span>
          </button>
        </>
      )}
    >
      <section className="rounded-3xl border border-white/10 bg-white/[0.045] p-5">
        {loading ? (
          <div className="py-8 text-sm text-white/50">{t("notifications.loading", "Loading notifications...")}</div>
        ) : items.length === 0 ? (
          <div className="py-8 text-sm text-white/50">{t("notifications.empty", "No notifications yet.")}</div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <div key={item.id} className={`rounded-2xl border p-4 ${item.status === "unread" ? "border-[#F37021]/35 bg-[#F37021]/8" : "border-white/10 bg-black/20"}`}>
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-semibold text-white">{item.title}</h2>
                      <span className="rounded-full border border-white/10 px-2 py-1 text-[11px] uppercase tracking-[0.16em] text-white/55">{item.severity || "info"}</span>
                      <span className="rounded-full border border-white/10 px-2 py-1 text-[11px] uppercase tracking-[0.16em] text-white/55">{item.status}</span>
                    </div>
                    <p className="mt-2 text-sm leading-6 text-white/62">{item.message}</p>
                    <div className="mt-2 text-xs text-white/38">{item.type || "system"} - {item.created_at?.slice?.(0, 19)} - {item.created_by || "system"}</div>
                  </div>
                  {item.status === "unread" && (
                    <button type="button" onClick={() => markRead(item.id)} className="h-10 shrink-0 rounded-full border border-white/10 px-4 text-sm font-semibold text-white/80 hover:border-[#F37021]/40 hover:text-white">
                      {t("notifications.markRead", "Mark read")}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </AdminShell>
  );
}

export default function AdminNotificationsPage() {
  return (
    <RequireAdmin permission="notifications.manage">
      <AdminNotificationsPageInner />
    </RequireAdmin>
  );
}
